<template>
  <div class="online-sync-health-overlay" @click.self="$emit('close')">
    <div class="online-sync-health-modal modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Sinkronizimi online</p>
        <button class="delete" aria-label="close" @click="$emit('close')"></button>
      </header>

      <section class="modal-card-body">
        <div v-if="loading" class="has-text-centered py-4">
          <i class="fa fa-spinner fa-spin"></i> Po analizohet...
        </div>
        <div v-else-if="error" class="notification is-danger is-light">{{ error }}</div>
        <div v-else-if="report">
          <div class="tabs is-boxed is-small mb-3">
            <ul>
              <li :class="{ 'is-active': activeTab === 'financa5' }">
                <a @click.prevent="activeTab = 'financa5'">Financa 5 (stok & çmime)</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'system' }">
                <a @click.prevent="activeTab = 'system'">Sistemi (kontroll gabimesh)</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'products' }">
                <a @click.prevent="activeTab = 'products'">Produktet ({{ productsAudit.length }})</a>
              </li>
            </ul>
          </div>

          <!-- Financa 5 tab -->
          <div v-show="activeTab === 'financa5'">
            <div class="columns is-multiline mb-3">
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Stoku Financa 5</p>
                  <p>
                    <span :class="f5.meta.stock_ok ? 'has-text-success' : 'has-text-danger'">{{ f5.meta.stock_ok ? "OK" : "Problem" }}</span>
                    — {{ f5.meta.stock_map_size }} kode
                  </p>
                  <p class="is-size-7 has-text-grey" v-if="f5.meta.stock_cache_age_seconds != null">Cache: {{ f5.meta.stock_cache_age_seconds }}s</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Çmimet Financa 5</p>
                  <p>
                    <span :class="f5.meta.price_ok ? 'has-text-success' : 'has-text-danger'">{{ f5.meta.price_ok ? "OK" : "Problem" }}</span>
                    — {{ f5.meta.price_map_size }} kode
                  </p>
                  <p class="is-size-7 has-text-grey" v-if="f5.meta.price_cache_age_seconds != null">Cache: {{ f5.meta.price_cache_age_seconds }}s</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Redis</p>
                  <p :class="f5.meta.redis_ok ? 'has-text-success' : 'has-text-danger'">{{ f5.meta.redis_ok ? "OK" : "Problem" }}</p>
                  <p class="is-size-7 has-text-grey" v-if="f5.meta.base_url">{{ f5.meta.base_url }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Përputhje stoku</p>
                  <p class="title is-5 mb-0">{{ f5.summary.stock_matched_percent }}%</p>
                  <p class="is-size-7">{{ f5.summary.stock_matched_count }} / {{ f5.summary.total_online_products }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Përputhje çmimi</p>
                  <p class="title is-5 mb-0">{{ f5.summary.price_matched_percent }}%</p>
                  <p class="is-size-7">{{ f5.summary.price_matched_count }} / {{ f5.summary.total_online_products }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Paketa kompozite</p>
                  <p class="title is-5 mb-0">{{ f5.summary.composite_count || 0 }}</p>
                </div>
              </div>
            </div>

            <sync-health-list-section
              title="Kode pa stok në Financa 5"
              :items="f5.missing_stock_codes"
              :total-count="f5MissingStockCount"
              :exporting="exportingSection === 'missing_stock_codes'"
              format="lines"
              @copy="copyLines(f5.missing_stock_codes, 'Kodet u kopjuan.')"
              @export="exportSection('missing_stock_codes', 'Pa stok Financa 5', 'lines')"
            />

            <sync-health-list-section
              v-if="f5MissingPriceCount > 0"
              title="Kode pa çmim në Financa 5"
              :items="f5.missing_price_codes"
              :total-count="f5MissingPriceCount"
              :exporting="exportingSection === 'missing_price_codes'"
              format="lines"
              class="mt-4"
              @copy="copyLines(f5.missing_price_codes, 'Kodet u kopjuan.')"
              @export="exportSection('missing_price_codes', 'Pa çmim Financa 5', 'lines')"
            />

            <div class="sync-section mt-4">
              <div class="sync-section-header">
                <h4 class="title is-6 mb-0">Shembuj / probleme ({{ f5.samples.length }})</h4>
                <button v-if="problemSamples.length" type="button" class="button is-small is-light" @click="copyProblemSamples">
                  <i class="fa fa-copy mr-1"></i> Kopjo problemet
                </button>
              </div>
              <div class="table-container sync-scroll-box mt-2">
                <table class="table is-fullwidth is-striped is-narrow is-size-7 mb-0">
                  <thead>
                    <tr>
                      <th>Kodi</th>
                      <th>Emri</th>
                      <th>Kompozit</th>
                      <th>Stok</th>
                      <th>Çmim</th>
                      <th>Përputhje</th>
                      <th>Arsyeja</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in f5.samples" :key="i">
                      <td>{{ row.code }}</td>
                      <td>{{ row.name }}</td>
                      <td>{{ row.is_composite ? "Po" : "Jo" }}</td>
                      <td>{{ row.stock != null ? row.stock : "—" }}</td>
                      <td>{{ row.price != null ? row.price : "—" }}</td>
                      <td>
                        <span :class="matchStatusClass(row.match_status)">{{ formatMatchStatus(row.match_status) }}</span>
                      </td>
                      <td>
                        <span :class="row.reason === 'ok' ? 'has-text-success' : 'has-text-danger'">{{ formatReason(row.reason) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="f5.composite_issues.length" class="sync-section mt-4">
              <div class="sync-section-header">
                <h4 class="title is-6 mb-0">Paketa kompozite — problem stoku ({{ f5.composite_issues.length }})</h4>
                <button type="button" class="button is-small is-light" @click="copyCompositeIssues">
                  <i class="fa fa-copy mr-1"></i> Kopjo
                </button>
              </div>
              <div v-for="(issue, i) in f5.composite_issues" :key="i" class="box py-2 mb-2 is-size-7">
                <strong>{{ issue.code }}</strong> — {{ issue.name }}
                <ul class="mt-1 ml-3">
                  <li v-for="(c, j) in issue.components" :key="j">
                    {{ c.code }} × {{ c.quantity }} — Financa 5: {{ c.in_erp ? c.erp_stock : "mungon" }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- System tab -->
          <div v-show="activeTab === 'system'">
            <p class="is-size-7 has-text-grey mb-3">
              Vetëm produktet që shfaqen në web (<strong>in_web</strong>, aktiv, jo të delistuara) — i njëjti filtër si thirrja publike.
              Kontrollohen kategoritë web, fotot, atributet, kodi, çmimi dhe paketat.
            </p>
            <div class="columns is-multiline mb-3">
              <div class="column is-4" v-for="card in systemSummaryCards" :key="card.key">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">{{ card.label }}</p>
                  <p
                    class="title is-5 mb-0"
                    :class="
                      card.key === 'total'
                        ? 'has-text-info'
                        : card.count > 0
                          ? 'has-text-danger'
                          : 'has-text-success'
                    "
                  >
                    {{ card.count }}
                  </p>
                </div>
              </div>
            </div>

            <p class="is-size-7 has-text-grey mb-2">
              Kategoritë web llogariten si në thirrjen publike: së pari lidhja e kategorisë së produktit me kategoritë online, pastaj kolonat
              <code>web_cat_1/2/3</code> të produktit vetëm si rezervë.
            </p>
            <sync-health-list-section
              title="Pa kategori web (edhe pas lidhjes kategori → online)"
              :items="system.missing_web_categories"
              :total-count="system.summary.missing_web_categories_count"
              :exporting="exportingSection === 'missing_web_categories'"
              format="web_categories"
              @copy="copyWebCategoryIssues(system.missing_web_categories, 'Lista u kopjua.')"
              @export="exportSection('missing_web_categories', 'Pa kategori web')"
            />

            <sync-health-list-section
              title="Pa foto (produkte që shkojnë në web)"
              :items="system.missing_images"
              :total-count="system.summary.missing_images_count"
              :exporting="exportingSection === 'missing_images'"
              format="products"
              class="mt-4"
              @copy="copyProducts(system.missing_images, 'Lista u kopjua.')"
              @export="exportSection('missing_images', 'Pa foto')"
            />

            <sync-health-list-section
              title="Pa atribute në kategori (kategoria nuk ka atribute të lidhura)"
              :items="system.no_category_attributes"
              :total-count="system.summary.no_category_attributes_count"
              :exporting="exportingSection === 'no_category_attributes'"
              format="attribute_issues"
              class="mt-4"
              @copy="copyAttributeIssues(system.no_category_attributes, 'Lista u kopjua.')"
              @export="exportSection('no_category_attributes', 'Pa atribute në kategori')"
            />

            <sync-health-list-section
              title="Me atribute në kategori por të paplotësuara"
              :items="system.unfilled_attributes"
              :total-count="system.summary.unfilled_attributes_count"
              :exporting="exportingSection === 'unfilled_attributes'"
              format="attribute_issues"
              class="mt-4"
              @copy="copyAttributeIssues(system.unfilled_attributes, 'Lista u kopjua.')"
              @export="exportSection('unfilled_attributes', 'Atribute të paplotësuara')"
            />

            <sync-health-list-section
              title="Pa kod produkti"
              :items="system.missing_code"
              :total-count="system.summary.missing_code_count"
              :exporting="exportingSection === 'missing_code'"
              format="products"
              class="mt-4"
              @copy="copyProducts(system.missing_code, 'Lista u kopjua.')"
              @export="exportSection('missing_code', 'Pa kod')"
            />

            <sync-health-list-section
              title="Pa çmim në sistem (0 ose bosh)"
              :items="system.missing_price"
              :total-count="system.summary.missing_price_count"
              :exporting="exportingSection === 'missing_price'"
              format="products"
              class="mt-4"
              @copy="copyProducts(system.missing_price, 'Lista u kopjua.')"
              @export="exportSection('missing_price', 'Pa çmim në sistem')"
            />

            <sync-health-list-section
              title="Paketë kompozite pa komponentë"
              :items="system.composite_no_components"
              :total-count="system.summary.composite_no_components_count"
              :exporting="exportingSection === 'composite_no_components'"
              format="products"
              class="mt-4"
              @copy="copyProducts(system.composite_no_components, 'Lista u kopjua.')"
              @export="exportSection('composite_no_components', 'Paketë bosh')"
            />
          </div>

          <!-- Products audit tab -->
          <div v-show="activeTab === 'products'">
            <div class="field mb-3">
              <div class="control has-icons-left">
                <input
                  v-model="productFilter"
                  class="input is-small"
                  type="text"
                  placeholder="Filtro sipas kodit ose emrit..."
                />
                <span class="icon is-small is-left"><i class="fa fa-search"></i></span>
              </div>
            </div>
            <div class="table-container sync-scroll-box sync-scroll-box-tall">
              <table class="table is-fullwidth is-striped is-narrow is-size-7 mb-0 sync-sticky-table">
                <thead>
                  <tr>
                    <th>Kodi</th>
                    <th>Emri</th>
                    <th>Kompozit</th>
                    <th>Stok F5</th>
                    <th>Çmim F5</th>
                    <th>Përputhje</th>
                    <th>Kategori web</th>
                    <th>Foto</th>
                    <th>Probleme</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in filteredProductsAudit" :key="row.guid || i">
                    <td>{{ row.code || "—" }}</td>
                    <td>{{ row.name }}</td>
                    <td>{{ row.is_composite ? "Po" : "Jo" }}</td>
                    <td>{{ row.f5_stock != null ? row.f5_stock : "—" }}</td>
                    <td>{{ row.f5_price != null ? row.f5_price : "—" }}</td>
                    <td>
                      <span :class="matchStatusClass(row.match_status)">{{ formatMatchStatus(row.match_status) }}</span>
                    </td>
                    <td>{{ formatWebCategories(row) }}</td>
                    <td :class="row.has_images ? 'has-text-success' : 'has-text-danger'">{{ row.has_images ? "Po" : "Jo" }}</td>
                    <td>
                      <span v-if="!(row.system_issues || []).length" class="has-text-success">—</span>
                      <span v-else class="has-text-danger">{{ formatSystemIssues(row) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="is-size-7 has-text-grey mt-2">{{ filteredProductsAudit.length }} / {{ productsAudit.length }} produkte</p>
          </div>

          <p v-if="copyMessage" class="notification is-success is-light is-size-7 mt-3 py-2">{{ copyMessage }}</p>
          <p v-if="exportError" class="notification is-danger is-light is-size-7 mt-3 py-2">{{ exportError }}</p>
          <p class="is-size-7 has-text-grey mt-2">Gjeneruar: {{ report.generated_at }}</p>
        </div>
      </section>

      <footer class="modal-card-foot">
        <button type="button" class="button" @click="$emit('close')">Mbyll</button>
        <button type="button" class="button is-light" :disabled="loading || exportingExcel" @click="downloadExcel">
          <i v-if="exportingExcel" class="fa fa-spinner fa-spin mr-1"></i>
          <i v-else class="fa fa-file-excel-o mr-1"></i>
          Shkarko Excel
        </button>
        <button type="button" class="button is-light" :disabled="loading || exportingJson" @click="downloadJson">
          <i v-if="exportingJson" class="fa fa-spinner fa-spin mr-1"></i>
          <i v-else class="fa fa-file-code-o mr-1"></i>
          Shkarko JSON (web)
        </button>
        <button type="button" class="button is-link" :disabled="loading || exportingExcel || exportingJson" @click="load">Rifresko</button>
      </footer>
    </div>
  </div>
</template>

<script>
import ProductsServices from "@/services/Product";
import SyncHealthListSection from "./sync-health-list-section.vue";
import { exportSyncHealthSection } from "@/utils/syncHealthExport";

const REASON_LABELS = {
  ok: "OK",
  code_not_in_financa5: "Kodi nuk gjendet në Financa 5",
  empty_product_code: "Pa kod",
  composite_no_components: "Paketë pa komponentë",
  composite_component_missing: "Komponent mungon në Financa 5",
};

const MATCH_STATUS_LABELS = {
  ok: "OK",
  partial: "Pjesërisht",
  missing: "Problem",
};

export default {
  components: { SyncHealthListSection },
  emits: ["close"],
  data() {
    return {
      loading: true,
      error: null,
      report: null,
      activeTab: "financa5",
      productFilter: "",
      copyMessage: "",
      copyMessageTimer: null,
      exportError: "",
      exportingExcel: false,
      exportingJson: false,
      exportingSection: "",
    };
  },
  computed: {
    f5() {
      return this.report?.financa5 || { meta: {}, summary: {}, samples: [], missing_stock_codes: [], missing_price_codes: [], composite_issues: [] };
    },
    system() {
      return (
        this.report?.system || {
          summary: {},
          missing_web_categories: [],
          missing_images: [],
          missing_code: [],
          missing_price: [],
          composite_no_components: [],
          no_category_attributes: [],
          unfilled_attributes: [],
        }
      );
    },
    productsAudit() {
      return this.report?.products_audit || [];
    },
    filteredProductsAudit() {
      const q = (this.productFilter || "").trim().toLowerCase();
      if (!q) return this.productsAudit;
      return this.productsAudit.filter((row) => {
        const code = String(row.code || "").toLowerCase();
        const name = String(row.name || "").toLowerCase();
        return code.includes(q) || name.includes(q);
      });
    },
    problemSamples() {
      return (this.f5.samples || []).filter((r) => r.reason && r.reason !== "ok");
    },
    f5MissingStockCount() {
      return this.productsAudit.filter((p) => p.f5_reason === "code_not_in_financa5").length;
    },
    f5MissingPriceCount() {
      return this.productsAudit.filter(
        (p) => p.f5_reason !== "skipped" && p.f5_reason !== "empty_product_code" && p.code && !p.f5_price_ok
      ).length;
    },
    systemSummaryCards() {
      const s = this.system.summary || {};
      return [
        { key: "total", label: "Në web (aktiv)", count: s.total_published_online || 0 },
        { key: "web", label: "Pa kategori web", count: s.missing_web_categories_count || 0 },
        { key: "img", label: "Pa foto", count: s.missing_images_count || 0 },
        { key: "noattr", label: "Pa atribute në kategori", count: s.no_category_attributes_count || 0 },
        { key: "unfilled", label: "Atribute të paplotësuara", count: s.unfilled_attributes_count || 0 },
        { key: "code", label: "Pa kod", count: s.missing_code_count || 0 },
        { key: "price", label: "Pa çmim", count: s.missing_price_count || 0 },
        { key: "comp", label: "Paketë bosh", count: s.composite_no_components_count || 0 },
      ];
    },
  },
  async mounted() {
    await this.load();
  },
  beforeUnmount() {
    if (this.copyMessageTimer) clearTimeout(this.copyMessageTimer);
  },
  methods: {
    formatReason(reason) {
      return REASON_LABELS[reason] || reason;
    },
    formatMatchStatus(status) {
      return MATCH_STATUS_LABELS[status] || status || "—";
    },
    matchStatusClass(status) {
      if (status === "ok") return "has-text-success";
      if (status === "partial") return "has-text-warning";
      return "has-text-danger";
    },
    formatWebCategories(row) {
      const parts = [row.web_cat_1, row.web_cat_2, row.web_cat_3].filter((v) => v != null && String(v).trim() !== "");
      return parts.length ? parts.join(" > ") : "—";
    },
    formatSystemIssues(row) {
      const issues = [...(row.system_issues || [])];
      if ((row.missing_attributes || []).length) {
        issues.push(`Atribute: ${row.missing_attributes.join(", ")}`);
      }
      return issues.join("; ") || "—";
    },
    showCopyFeedback(message) {
      this.copyMessage = message;
      if (this.copyMessageTimer) clearTimeout(this.copyMessageTimer);
      this.copyMessageTimer = setTimeout(() => {
        this.copyMessage = "";
      }, 2500);
    },
    async copyToClipboard(text, successMessage) {
      if (!text) {
        this.showCopyFeedback("Nuk ka të dhëna.");
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        this.showCopyFeedback(successMessage);
      } catch (e) {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand("copy");
          this.showCopyFeedback(successMessage);
        } catch (err) {
          this.showCopyFeedback("Kopjimi dështoi.");
        }
        document.body.removeChild(ta);
      }
    },
    copyLines(lines, msg) {
      this.copyToClipboard((lines || []).join("\n"), msg);
    },
    copyProducts(products, msg) {
      const text = (products || []).map((p) => `${p.code || "—"}\t${p.name || ""}`).join("\n");
      this.copyToClipboard(text, msg);
    },
    copyAttributeIssues(items, msg) {
      const text = (items || [])
        .map((p) => {
          const missing = (p.missing || []).join(", ");
          const reason = p.reason ? ` (${p.reason})` : "";
          const tail = missing ? `\t→ ${missing}` : reason;
          return `${p.code || "—"}\t${p.name || ""}${tail}`;
        })
        .join("\n");
      this.copyToClipboard(text, msg);
    },
    copyWebCategoryIssues(items, msg) {
      const text = (items || [])
        .map((p) => {
          const eff = [p.effective_web_cat_1, p.effective_web_cat_2, p.effective_web_cat_3]
            .filter((v) => v != null && String(v).trim() !== "")
            .join(" > ");
          const stored = [p.stored_web_cat_1, p.stored_web_cat_2, p.stored_web_cat_3]
            .filter((v) => v != null && String(v).trim() !== "" && String(v).trim() !== "[Pa Percaktuar]")
            .join(" > ");
          return `${p.code || "—"}\t${p.name || ""}\tEfektive: ${eff || "—"}\tKolona: ${stored || "—"}`;
        })
        .join("\n");
      this.copyToClipboard(text, msg);
    },
    copyProblemSamples() {
      const lines = this.problemSamples.map(
        (r) =>
          `${r.code}\t${r.name}\t${r.is_composite ? "Po" : "Jo"}\t${this.formatMatchStatus(r.match_status)}\t${this.formatReason(r.reason)}\t${r.stock ?? ""}\t${r.price ?? ""}`
      );
      this.copyToClipboard(lines.join("\n"), "Problemet u kopjuan.");
    },
    copyCompositeIssues() {
      const lines = [];
      (this.f5.composite_issues || []).forEach((issue) => {
        lines.push(`${issue.code} — ${issue.name}`);
        (issue.components || []).forEach((c) => {
          lines.push(`  ${c.code} x${c.quantity} — F5: ${c.in_erp ? c.erp_stock : "mungon"}`);
        });
      });
      this.copyToClipboard(lines.join("\n"), "Paketat u kopjuan.");
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        this.report = await ProductsServices.getOnlineSyncHealth();
      } catch (e) {
        this.error = e?.response?.data?.message || "Gabim gjatë leximit të raportit.";
      } finally {
        this.loading = false;
      }
    },
    async downloadExcel() {
      this.exportError = "";
      this.exportingExcel = true;
      try {
        await ProductsServices.exportOnlineSyncHealth();
        this.showCopyFeedback("Excel u shkarkua.");
      } catch (e) {
        this.exportError = e?.message || "Gabim gjatë shkarkimit të Excel.";
      } finally {
        this.exportingExcel = false;
      }
    },
    async downloadJson() {
      this.exportError = "";
      this.exportingJson = true;
      try {
        await ProductsServices.exportOnlineProductListJson();
        this.showCopyFeedback("JSON u shkarkua.");
      } catch (e) {
        this.exportError = e?.message || "Gabim gjatë shkarkimit të JSON.";
      } finally {
        this.exportingJson = false;
      }
    },
    exportSection(sectionKey, title, format) {
      this.exportError = "";
      this.exportingSection = sectionKey;
      try {
        exportSyncHealthSection(this.productsAudit, sectionKey, title, format);
        this.showCopyFeedback("Excel u shkarkua.");
      } catch (e) {
        this.exportError = e?.message || "Gabim gjatë shkarkimit të Excel.";
      } finally {
        this.exportingSection = "";
      }
    },
  },
};
</script>

<style scoped>
.online-sync-health-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(10, 10, 10, 0.52);
  overflow-y: auto;
}
.online-sync-health-modal {
  width: 100%;
  max-width: 1100px;
  max-height: calc(100vh - 3rem);
  display: flex;
  flex-direction: column;
  margin: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
.online-sync-health-modal .modal-card-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.sync-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.sync-scroll-box {
  max-height: 240px;
  overflow: auto;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}
.sync-scroll-box-tall {
  max-height: 400px;
}
.sync-sticky-table thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  box-shadow: inset 0 -1px 0 #dbdbdb;
}
.has-text-warning {
  color: #b86e00;
}
</style>
